/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Box, Chip, Grid, TextField, Autocomplete, FormControl, FormHelperText } from '@mui/material'
import Typography from '@mui/material/Typography'
import Joi, { ValidationError } from 'joi'
import React from 'react'
import { StepComponentProps } from 'react-step-builder'
// eslint-disable-next-line import/extensions
import * as countries from '../../common/countries.json'
import stepIcon from '../../images/step-illustration-local.png'
import analytics from '../../libs/analytics'
import Navigation from './common/Navigation'
import OnboardStepper from './common/OnboardStepper'
import { handleBasicValueChanges, setBasicValueTouched } from './common/formHelpers'

type Country = {
  name: string
  code: string
}

class StepLocations extends React.PureComponent<StepComponentProps> {
  state: {
    countriesValue: Country[]
    countriesPopularValue: Country[]
    popularCountries: string[]
    formErrors: { [key: string]: ValidationError | undefined }
    touchedFields: Set<string>
    formValidated: boolean
  } = {
    countriesValue: [],
    countriesPopularValue: [],
    popularCountries: ['US', 'GB', 'WW'],
    formErrors: {},
    touchedFields: new Set(),
    formValidated: false,
  }

  render() {
    const { countriesValue, countriesPopularValue, formErrors, touchedFields, formValidated } = this.state
    return (
      <Grid container direction="column">
        <Grid item sx={{ mb: { xs: 3, sm: 4, md: 4 } }}>
          <OnboardStepper activeStep={3} />
        </Grid>
        <Grid item>
          <Grid container direction="row" spacing={5} justifyContent="spaceBetween">
            <Grid item xs={12} sm={12} md={7}>
              <Box>
                <Typography variant="h1">Localize your app</Typography>
                <Typography variant="subtitle1">
                  Select the countries where your employees are located to give them a personalized experience. You can
                  select more than one and if you are not sure, select worldwide.
                </Typography>
              </Box>
              <Box sx={{ mb: 4 }}>
                <Box mt={1}>
                  <Typography variant="h3" sx={{ mt: 2 }}>
                    Select from popular
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    {countriesPopularValue.map((v: Country) => (
                      <Chip key={v.name} label={v.name} sx={{ mb: 1, mr: 1 }} onClick={this.onPopularChipClick(v)} />
                    ))}
                  </Box>
                </Box>
                <FormControl
                  required
                  error={!!formErrors.locations && (touchedFields.has('locations') || formValidated)}
                  variant="standard"
                  sx={{ maxWidth: 500, width: '100%' }}
                >
                  <FormHelperText>
                    {(touchedFields.has('locations') || formValidated) && formErrors.locations?.message}
                  </FormHelperText>
                  <Autocomplete
                    multiple
                    id="locations"
                    options={this.countries}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={countriesValue}
                    onBlur={(e) => setBasicValueTouched(e.target.id, this)}
                    onChange={(e, newValue) => this.onListChange(newValue)}
                    renderTags={() => null}
                    renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Search" />}
                  />
                </FormControl>

                <div
                  css={css`
                    margin-top: 10px;
                  `}
                >
                  {countriesValue.map((v: Country) => (
                    <Chip
                      key={v.name}
                      label={v.name}
                      onDelete={this.onChipDelete(v.code)}
                      sx={{ mb: 1, mt: 1, mr: 1 }}
                    />
                  ))}
                </div>
              </Box>
            </Grid>
            <Grid item md={5} sx={{ mb: 6, display: { xs: 'none', sm: 'none', md: 'block' } }}>
              <img src={stepIcon} alt="Step local" width={302} height={260} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {Navigation({
            props: this.props,
            component: this,
            validationSchema: this.validationSchema,
            nextCallback: this.onNext,
          })}
        </Grid>
      </Grid>
    )
  }

  onNext = () => {
    const { countriesValue } = this.state
    const locations = countriesValue.map((country) => country.code)

    analytics.sendEvent('Onboard@LocationsProvided', { locations })

    return false // important
  }

  validationSchema = Joi.object({
    locations: Joi.array().items(Joi.object().required()).required().label('Locations').messages({
      'array.includesRequiredUnknowns': 'Select at least one location or "World wide"',
      'any.required': 'Select at least one location or "Worldwide"',
    }),
  })
    .options({
      abortEarly: false,
    })
    .pattern(/.+/, Joi.allow())

  // countries file has to be any
  countries: Country[] = (countries as any).default as Country[]

  onPopularChipClick(country: Country) {
    const { countriesValue } = this.state
    return () => {
      if (!countriesValue.find((v) => v.code === country.code)) {
        this.updateCountriesList(countriesValue.concat(country))
      }
    }
  }

  updateCountriesList(newValue: Country[]) {
    this.setState({
      countriesValue: newValue,
    })
    handleBasicValueChanges('locations', newValue, this)
  }

  onListChange(newValue: Country[]) {
    this.updateCountriesList(newValue)
  }

  onChipDelete(code) {
    const { countriesValue } = this.state
    return () => {
      const newValue = countriesValue.filter((v: Country) => v.code !== code)
      this.setState({
        countriesValue: newValue,
      })
      setBasicValueTouched('locations', this)
      handleBasicValueChanges('locations', newValue, this)
    }
  }

  customSortDefaultCountries(a: Country, b: Country): number {
    if (a.code === 'US' && b.code === 'GB') {
      return -1
    }
    if (b.code === 'WW') {
      return -1
    }
    return a.code < b.code ? 1 : -1
  }

  componentDidMount() {
    analytics.sendEvent('page@StepLocations')

    const { popularCountries } = this.state
    const countriesPopularValue = this.countries.filter((v: Country) => popularCountries.includes(v.code))
    countriesPopularValue.sort(this.customSortDefaultCountries)

    this.setState({
      countriesPopularValue,
      countriesValue: this.props.getState('locations', []),
    })
  }
}

export default StepLocations
